/* eslint-disable no-unused-vars */
import { useMemo, memo, useEffect, useCallback } from 'react';
import * as S from './ContentItem.styled';
import { useSignal } from '@preact/signals-react';
import {
  ParagraphWithTooltip,
  CopyString,
  Bid,
  ImageWithFallback,
  Message,
  RadioBox,
  DropDownSelect,
} from 'components';
import { Palette } from 'config';
import { ProgressBar } from '../progressBar/ProgressBar';
import { MsgToUser } from '../msgToUser/MsgToUser';
import { GeoBudget } from '../geoBudget/GeoBudget';

const chooseMsgToUser = {
  progressBar: ProgressBar,
  errorMsg: MsgToUser,
};

export const ContentItem = memo(
  ({
    block,
    budgetToUse,
    monthlyBudget,
    pbaName,
    packageName,
    pbaNumbers,
    uniqueId,
    countries,
    isOpenBudget,
    isAgency,
    isBudgetPerGeo,
    usedBudget,
    // currentCost, (replaced with usedBudget)
    activeCampaigns,
    // allCountries,
    renderedCountries,
    countriesBudgetItems,
    onChangeMonthlyBudget,
    onUpdateGeoBudgetItems,
    onUpdateIsOpenBudget,
    onUpdateIsAgency,
    onUpdateIsBudgetPerGeo,
    img,
    searchValue,
    isLastElement,
    isFirstElement,
    isEdit,
    handleChangeIsEdit,
    // indexErrorItems$,
    index,
    // isScrolling,
  }) => {
    const monthlyBudgetValue$ = useSignal(monthlyBudget === 0 ? '' : monthlyBudget);

    const msgToUser = useMemo(() => {
      if (monthlyBudget > 0 && budgetToUse < 0) {
        return { type: 'errorMsg', textError: '$0 left' };
      } else if (monthlyBudget > 0 && !isAgency && !isOpenBudget) {
        return {
          type: 'progressBar',
          id: uniqueId,
          max: monthlyBudget,
          value: monthlyBudget - usedBudget,
          label: `$${(monthlyBudget - usedBudget).toFixed(2).replace(/\.00$/, '')} left`,
        };
      } else {
        const costText = usedBudget ? usedBudget.toFixed(2).replace(/\.00$/, '') : '0';
        return {
          type: 'errorMsg',
          textError: `Current monthly \n cost: ${costText}$`,
        };
      }
    }, [monthlyBudget, budgetToUse, usedBudget, uniqueId, isAgency, isOpenBudget]);

    const showNotification = useMemo(() => {
      if (isAgency || isOpenBudget || isBudgetPerGeo) return false;
      if (!monthlyBudget || monthlyBudget < 1000) return false;
      return monthlyBudget < budgetToUse;
    }, [monthlyBudget, budgetToUse, isBudgetPerGeo, isAgency, isOpenBudget]);

    useEffect(() => {
      const newValue = isEdit ? monthlyBudget : monthlyBudget === 0 ? '' : monthlyBudget;
      monthlyBudgetValue$.value = newValue;
    }, [monthlyBudget, isEdit]);

    const activeCampaignsToShow = useMemo(() => {
      // TO-DO if no actuve campaigns why do we show 0???
      return activeCampaigns ? activeCampaigns : 0;
    }, [activeCampaigns]);

    const handleChangeMb = useCallback(
      (e) => {
        const newMonthlyBudget = e.target.value;
        if (newMonthlyBudget === '') {
          handleChangeIsEdit(uniqueId, false);
          monthlyBudgetValue$.value = '';
        } else {
          handleChangeIsEdit(uniqueId, true);
        }

        if (newMonthlyBudget.toString().length > 6) return;

        monthlyBudgetValue$.value = newMonthlyBudget;
        onChangeMonthlyBudget(uniqueId, +newMonthlyBudget);
      },
      [handleChangeIsEdit, uniqueId, onChangeMonthlyBudget, monthlyBudgetValue$, isEdit],
    );

    const showErrorMsg = useCallback(() => {
      return (
        (isEdit || (!isEdit && isBudgetPerGeo)) &&
        monthlyBudget &&
        monthlyBudget < 1000 &&
        !isOpenBudget &&
        !isAgency
      );
    }, [isEdit, monthlyBudget, isBudgetPerGeo, isOpenBudget, isAgency]);

    // const shouldShowError = showErrorMsg();

    // useEffect(() => {
    //   const indexExists = indexErrorItems$.value.includes(index);
    //   if (shouldShowError && !indexExists) {
    //     indexErrorItems$.value = [...indexErrorItems$.value, index];
    //   } else if (!shouldShowError && indexExists) {
    //     indexErrorItems$.value = indexErrorItems$.value.filter((ind) => ind !== index);
    //   }
    // }, [shouldShowError, index, indexErrorItems$]);

    const MessageToUser = chooseMsgToUser[msgToUser.type];

    return (
      <>
        <S.Container isFirstElement={isFirstElement} isBudgetPerGeo={isBudgetPerGeo}>
          <S.App>
            <S.NameWrap>
              <ImageWithFallback
                width={56}
                height={56}
                src={`https://cdn.appnext.com/apps/${img}`}
              />
              <S.AppData>
                <S.PbaName>
                  <ParagraphWithTooltip text={pbaName} search={searchValue} isHighlight={true} />
                </S.PbaName>
                <S.PackageName>
                  <ParagraphWithTooltip text={packageName} />
                </S.PackageName>
                <S.IdsWrap>
                  {pbaNumbers &&
                    pbaNumbers
                      .split(',')
                      .slice(0, 3)
                      .map((id, ind) => (
                        <CopyString
                          label={ind === 0 ? 'ID' : ''}
                          key={id + ind}
                          text={id.toString()}
                        />
                      ))}
                  {pbaNumbers &&
                    pbaNumbers.split(',').length > 3 &&
                    `+${pbaNumbers.split(',')?.length - 3}`}
                </S.IdsWrap>
              </S.AppData>
            </S.NameWrap>
            {/* TO-DO unable countries */}
            <S.Countries>{/* renderedCountries */}</S.Countries>
            <S.ActiveCampaigns>{activeCampaignsToShow}</S.ActiveCampaigns>
            <S.BudgetWrap>
              <div className={isBudgetPerGeo ? '' : 'no-open'}>
                <Bid.Big
                  onChange={handleChangeMb}
                  value={monthlyBudgetValue$.value}
                  withError={false}
                  width={60}
                  style={
                    showErrorMsg()
                      ? isBudgetPerGeo
                        ? {
                            border: `1px solid ${Palette.Red._300}`,
                            borderRadius: 'calc(4px + 1px)',
                            overflow: 'hidden',
                            opacity: '0.5',
                            pointerEvents: 'none',
                          }
                        : {
                            border: `1px solid ${Palette.Red._300}`,
                            borderRadius: 'calc(4px + 1px)',
                            overflow: 'hidden',
                          }
                      : showNotification
                      ? isBudgetPerGeo
                        ? {
                            border: `1px solid ${Palette.Orange._300}`,
                            borderRadius: 'calc(4px + 1px)',
                            overflow: 'hidden',
                            opacity: '0.5',
                            pointerEvents: 'none',
                          }
                        : {
                            border: `1px solid ${Palette.Orange._300}`,
                            borderRadius: 'calc(4px + 1px)',
                            overflow: 'hidden',
                          }
                      : isBudgetPerGeo || isAgency || isOpenBudget
                      ? { opacity: '0.5', pointerEvents: 'none' }
                      : {}
                  }
                />
              </div>
              {showErrorMsg() ? (
                <S.ErrorMsg>
                  Minimum <br /> Budget 1000$
                </S.ErrorMsg>
              ) : (
                <MessageToUser {...msgToUser}>
                  {msgToUser.type === 'errorMsg' ? msgToUser.textError : null}
                </MessageToUser>
              )}
            </S.BudgetWrap>
            {/* <S.RadioButtonsWrap>
              <RadioBox.NormalSize
                label='Open Budget'
                width={80}
                variant='left'
                onChange={() => onUpdateIsOpenBudget(uniqueId, !isOpenBudget)}
                pressed={isOpenBudget}
                value={isOpenBudget}
              />
              <RadioBox.NormalSize
                label='Self Service / Agency'
                width={110}
                variant='center'
                onChange={() => onUpdateIsAgency(uniqueId, !isAgency)}
                pressed={isAgency}
                value={isOpenBudget}
                style={{ flexBasis: '110px' }}
              />
              <RadioBox.NormalSize
                label='Budget per Geo'
                width={80}
                variant='right'
                onChange={() => onUpdateIsBudgetPerGeo(uniqueId, !isBudgetPerGeo)}
                pressed={isBudgetPerGeo}
                value={isOpenBudget}
              />
            </S.RadioButtonsWrap> */}
          </S.App>
          {showNotification && (
            <S.MessageBlock>
              <Message type={Message.messageType.warning} isOpen={showNotification}>
                The new budget is lower than the current spent, the new budget will start in the
                next month.
              </Message>
            </S.MessageBlock>
          )}
          {/* isBudgetPerGeo && (
            <GeoBudget
              allCountries={allCountries}
              onChangeMonthlyBudget={onChangeMonthlyBudget}
              countriesBudgetItems={countriesBudgetItems}
              uniqueId={uniqueId}
              onUpdateGeoBudgetItems={onUpdateGeoBudgetItems}
            />
          ) */}
        </S.Container>
        {isLastElement && <div style={{ height: '24px' }} />}
      </>
    );
  },
);

ContentItem.displayName = 'ContentItem';
